import {useStripe, useElements, PaymentElement, AddressElement} from '@stripe/react-stripe-js';
import React, {useEffect, useState} from 'react';
import {Button, CircularProgress, Typography} from "@material-ui/core";
import _ from 'lodash/fp';

const CheckoutForm = ({
    amount,
    teamId,
    teamName,
    transactionType,
    createStripeConfirmationQuote = _.noop,
    finalizeStripePayment = _.noop,
    onProcessStripePayment = _.noop,
    onStripePaymentPending = _.noop,
    payProcessingFees = true,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [quote, setQuote] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [isBusy, setBusy] = useState(false)

    useEffect(() => {
        setQuote(null)
    }, [amount, payProcessingFees])

    const handleSubmit = async (event) => {
        // We don't want to let default form submission happen here,
        // which would refresh the page.
        event.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setBusy(true)
        setErrorMessage(null)

        try {
            if (!quote) {
                const submitResult = await elements.submit()
                if (submitResult.error) {
                    throw new Error(submitResult.error.message)
                }

                const confirmationTokenResult = await stripe.createConfirmationToken({elements})
                if (confirmationTokenResult.error) {
                    throw new Error(confirmationTokenResult.error.message)
                }

                const nextQuote = await createStripeConfirmationQuote({
                    confirmationTokenId: confirmationTokenResult.confirmationToken.id,
                    amount,
                    teamId,
                    teamName,
                    transactionType,
                })
                if (!nextQuote) {
                    throw new Error('Unable to calculate the Stripe processing fee')
                }
                const adjustedQuote = payProcessingFees ? {
                    ...nextQuote,
                    processingFeesPaid: true,
                } : {
                    ...nextQuote,
                    feeAmount: 0,
                    grossAmount: amount,
                    feePolicy: 'NO_FEE',
                    rate: 0,
                    fixedFee: 0,
                    processingFeesPaid: false,
                }
                setQuote({
                    ...adjustedQuote,
                    confirmationTokenId: confirmationTokenResult.confirmationToken.id,
                })
                return
            }

            const paymentIntent = await finalizeStripePayment({
                ...quote,
                returnUrl: window.location.href,
            })
            if (!paymentIntent) {
                throw new Error('Unable to finalize the Stripe payment')
            }
            let completedPaymentIntent = paymentIntent
            if (paymentIntent?.status === 'requires_action') {
                await onStripePaymentPending()
                const nextActionResult = await stripe.handleNextAction({clientSecret: paymentIntent.clientSecret})
                if (nextActionResult.error) {
                    throw new Error(nextActionResult.error.message)
                }
                completedPaymentIntent = nextActionResult.paymentIntent
            }

            onProcessStripePayment(completedPaymentIntent, quote)
        } catch (error) {
            setErrorMessage(error.message)
        } finally {
            setBusy(false)
        }
    };

    return (
        <div>
            <PaymentElement />
            <AddressElement options={{mode: 'billing'}} />
            {quote &&
                <Typography style={{margin: '1rem 0 0 0'}}>
                    <strong>Stripe total: HK${Number(quote.grossAmount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong> {payProcessingFees ? `(includes estimated HK$${Number(quote.feeAmount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} processing fee)` : '(excludes processing fee)'}
                </Typography>
            }
            {errorMessage && <Typography color="error" style={{marginTop: '1rem'}}>{errorMessage}</Typography>}
            <Button onClick={handleSubmit} color="primary" size="large" style={{marginTop: "1rem"}} variant="contained" disabled={!stripe || isBusy}>
                {isBusy ? <CircularProgress size={24}/> : quote ? 'Confirm Payment' : 'Review Payment Total'}
            </Button>
        </div>
    )
};

export default CheckoutForm;